// Many writes of a kind, one card — T76 (17 Sep 2026), agents doc § 7.5.
//
// "Send these eight a message" comes back from the brain API as eight cards, each applied by its
// own Confirm as a card always was (services/bff `actionGroups.ts`): what they share is a `group`
// key — the answer that proposed them and the tool it proposed them through. This file is the
// panel's half, kept out of the component so it is tested without a browser: which cards are drawn
// as one, and what the outcome of eight applies is said to be. Two or more cards of a key are a group; one card of a key is the card it always was.
import type { ActionCard } from '@vera/bff';
import { resultsTellNoteHeld } from '@vera/db/research-review';

/** What the panel draws under a turn: a card on its own, or the cards of one kind as one card. */
export type CardUnit =
  | { kind: 'card'; card: ActionCard }
  | { kind: 'group'; key: string; tool: string; cards: ActionCard[] };

/** The units of a turn, each where its first card was proposed; a group keeps the model's order. */
export function cardUnits(cards: readonly ActionCard[]): CardUnit[] {
  const byKey = new Map<string, ActionCard[]>();
  for (const card of cards) {
    if (card.group === null) continue;
    byKey.set(card.group, [...(byKey.get(card.group) ?? []), card]);
  }
  const units: CardUnit[] = [];
  const drawn = new Set<string>();
  for (const card of cards) {
    const members = card.group === null ? null : byKey.get(card.group)!;
    if (!members || members.length < 2) {
      units.push({ kind: 'card', card });
    } else if (!drawn.has(card.group!)) {
      drawn.add(card.group!);
      // The key is `<query id>:<tool>` (the API's `groupOf`); the tool is what the head names.
      const tool = card.group!.slice(card.group!.indexOf(':') + 1);
      units.push({ kind: 'group', key: card.group!, tool, cards: members });
    }
  }
  return units;
}

/** The things a group is made of, as its head counts them: "8 messages", "3 notes". */
const NOUNS: Readonly<Record<string, readonly [string, string]>> = {
  send_message: ['message', 'messages'],
  send_follow_up: ['follow-up', 'follow-ups'],
  send_first_message: ['first message', 'first messages'],
  add_note: ['note', 'notes'],
  add_client_note: ['note', 'notes'],
  add_to_search: ['move', 'moves'],
  move_client: ['move', 'moves'],
  move_talent_client: ['move', 'moves'],
  // T365f: a Search's own stages.
  add_search_stage: ['stage to add', 'stages to add'],
  edit_search_stage: ['stage change', 'stage changes'],
  tag_candidate: ['tag', 'tags'],
  send_invitation: ['invitation', 'invitations'],
  resend_invitation: ['invitation', 'invitations'],
  put_forward: ['put-forward', 'put-forwards'],
  pause_vera: ['pause', 'pauses'],
  resume_vera: ['resume', 'resumes'],
  enter_candidate: ['record', 'records'],
  // T82: the removals, each a card a tap confirms.
  delete: ['deletion', 'deletions'],
  remove_from_search: ['removal', 'removals'],
  untag_candidate: ['tag to take off', 'tags to take off'],
  find_contact: ['lookup', 'lookups'],
  accept_proposal: ['proposal to accept', 'proposals to accept'],
  reject_proposal: ['proposal to reject', 'proposals to reject'],
  // T139c: her grade cards.
  correct_grade: ['grade', 'grades'],
  object_to_grading: ['objection', 'objections'],
  lift_grading_objection: ['objection to lift', 'objections to lift'],
};

export function groupNoun(tool: string, count: number): string {
  const [one, many] = NOUNS[tool] ?? ['action', 'actions'];
  return `${count} ${count === 1 ? one : many}`;
}

export interface GroupTally {
  total: number;
  proposed: number;
  applying: number;
  waiting: number;
  applied: number;
  failed: number;
  dismissed: number;
  /** T239: closed when a newer card went up in the same WhatsApp chat. */
  replaced: number;
}

export function tallyOf(cards: readonly ActionCard[]): GroupTally {
  const tally: GroupTally = {
    total: cards.length,
    proposed: 0,
    applying: 0,
    waiting: 0,
    applied: 0,
    failed: 0,
    dismissed: 0,
    replaced: 0,
  };
  for (const card of cards) tally[card.status] += 1;
  return tally;
}

/**
 * T413: what kadmon says under the cards once a yes has gone through, the same words as on
 * WhatsApp (bff `decidedReply`): one line for a card; for a group, "Done, all 3 moved to
 * Interviewed." when every card is applied and did the same, else a line a card that went through.
 * The words are the server's (`ActionCard.done`, `did`); nothing is worded here.
 */
export function doneLinesOf(cards: readonly ActionCard[]): string[] {
  const applied = cards.filter((card) => card.status === 'applied' && card.done);
  const did = applied[0]?.did;
  if (
    applied.length > 1 &&
    applied.length === cards.length &&
    did &&
    applied.every((c) => c.did === did) &&
    // T126h: a turn-down that has to tell the recruiter something is said on its own line.
    !applied.some((c) => resultsTellNoteHeld(c.results))
  ) {
    return [`Done, all ${applied.length} ${did}.`];
  }
  return applied.map((card) => card.done!);
}

/**
 * One tap, every card of the group: each through its own Confirm, one after another in the order
 * they were proposed — two at once would race on their own order (realtime.ts says the same of
 * tool calls). A card somebody else had already decided (`skip`) is passed over as `null`; a
 * refusal is not an error — it comes back as the card, `failed`, with its reason — so one message
 * whose window has closed never stops the seven behind it. Anything else (the network) stops the
 * run where it is: the cards not reached are still up, and the panel says so. `each` — the chat
 * refetched, so the card shows how far the group has got — is started after each card and not
 * waited for (24 Sep 2026): the next card goes at once, where eight waited on eight refetches.
 */
export async function confirmEach(
  ids: readonly string[],
  confirm: (id: string) => Promise<ActionCard>,
  skip: (error: unknown) => boolean,
  each?: () => unknown,
): Promise<(ActionCard | null)[]> {
  const cards: (ActionCard | null)[] = [];
  for (const id of ids) {
    try {
      cards.push(await confirm(id));
    } catch (error) {
      if (!skip(error)) throw error;
      cards.push(null);
    }
    void each?.();
  }
  return cards;
}
